/**
 * Loyalty Display Settings tab — per-tenant toggles for where loyalty UI appears.
 *
 * Controls visibility of loyalty features across the storefront:
 * - Cart slide-in (tier progress bar)
 * - Checkout (points redemption section)
 * - Account / Rewards hub page
 *
 * Follows the exact same pattern as StockManagementSettings.tsx:
 * useStorefrontTenantSettings() → optimistic update → revert on error.
 *
 * Plan 1833, Cross-Cutting: Loyalty Display Toggles
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useStorefrontTenantSettings } from "@/features/storefront/settings/useStorefrontTenantSettings";
import type { TenantSettingsResponse } from "@/lib/api/controlPlaneClient";
import {
  SettingRow,
  Divider,
} from "@/features/storefront/settings/SettingRow";

export function LoyaltyDisplaySettingsTab() {
  const { settings, isLoading, isError, updateAsync, isUpdating, queryKey } =
    useStorefrontTenantSettings();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isError) {
      toast.error("ไม่สามารถโหลดการตั้งค่าได้");
    }
  }, [isError]);

  const handleToggle = async (
    field: "loyalty_enabled" | "show_loyalty_in_cart" | "show_loyalty_in_checkout" | "show_loyalty_in_account",
    checked: boolean,
  ) => {
    const previousData =
      queryClient.getQueryData<TenantSettingsResponse>(queryKey);

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) => (old ? { ...old, [field]: checked ? 1 : 0 } : old),
    );

    try {
      await updateAsync({ [field]: checked ? 1 : 0 });
      toast("บันทึกแล้ว");
    } catch {
      queryClient.setQueryData(queryKey, previousData);
      toast.error("บันทึกไม่สำเร็จ กรุณาลองใหม่");
    }
  };

  const loyaltyOn = !!settings?.loyalty_enabled;

  return (
    <div className="mt-4 space-y-6">
      {/* Master switch */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 mb-2">
          เปิด-ปิดโปรแกรม
        </p>
        <Card className="rounded-2xl border-0">
          <CardContent className="p-0">
            <SettingRow
              label="เปิดใช้งานโปรแกรมสะสมแต้ม"
              subtitle="สวิตช์หลัก — เมื่อปิด ระบบสะสมแต้มทั้งหมดจะถูกซ่อนจากหน้าร้าน (สะสมแต้ม, แลกแต้ม, แสดงผล)"
            >
              {isLoading ? (
                <Skeleton className="w-9 h-5 rounded-full" />
              ) : (
                <Switch
                  checked={loyaltyOn}
                  onCheckedChange={(c) => handleToggle("loyalty_enabled", c)}
                  disabled={isUpdating}
                  aria-label="Enable loyalty program"
                />
              )}
            </SettingRow>
          </CardContent>
        </Card>
      </div>

      {/* Per-section display toggles */}
      <div className={loyaltyOn ? "" : "opacity-50 pointer-events-none"}>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 mb-2">
        การแสดงผลคะแนนสะสม
      </p>
      <Card className="rounded-2xl border-0">
        <CardContent className="p-0">
          {/* Cart */}
          <SettingRow label="แสดงในตะกร้าสินค้า" subtitle="แสดงแถบความคืบหน้าระดับสมาชิกและคะแนนที่จะได้รับในตะกร้า">
            {isLoading ? (
              <Skeleton className="w-9 h-5 rounded-full" />
            ) : (
              <Switch
                checked={!!settings?.show_loyalty_in_cart}
                onCheckedChange={(c) => handleToggle("show_loyalty_in_cart", c)}
                disabled={isUpdating}
                aria-label="Show loyalty in cart"
              />
            )}
          </SettingRow>

          <Divider />

          {/* Checkout */}
          <SettingRow label="แสดงในหน้าชำระเงิน" subtitle="ให้ลูกค้าใช้คะแนนแลกส่วนลดในหน้าชำระเงิน">
            {isLoading ? (
              <Skeleton className="w-9 h-5 rounded-full" />
            ) : (
              <Switch
                checked={!!settings?.show_loyalty_in_checkout}
                onCheckedChange={(c) =>
                  handleToggle("show_loyalty_in_checkout", c)
                }
                disabled={isUpdating}
                aria-label="Show loyalty in checkout"
              />
            )}
          </SettingRow>

          <Divider />

          {/* Account / Rewards Hub */}
          <SettingRow label="แสดงหน้ารางวัลในบัญชีลูกค้า" subtitle="แสดงหน้ารางวัล (/rewards) ในเมนูบัญชีของลูกค้า">
            {isLoading ? (
              <Skeleton className="w-9 h-5 rounded-full" />
            ) : (
              <Switch
                checked={!!settings?.show_loyalty_in_account}
                onCheckedChange={(c) =>
                  handleToggle("show_loyalty_in_account", c)
                }
                disabled={isUpdating}
                aria-label="Show loyalty in account"
              />
            )}
          </SettingRow>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
